import { IconShieldCheck } from '@iwoca/icons';
import { Button, IwocaLogo } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';

type ConsentPromptProps = {
    connectorName: string;
    permissions: string[];
    handleApprove: () => void;
    handleDeny: () => void;
    isLoading: boolean;
};

export const ConsentReviewPrompt = ({
    connectorName,
    permissions,
    handleApprove,
    handleDeny,
    isLoading
}: ConsentPromptProps) => {
    return (
        <div className="flex min-h-screen items-center justify-center p-l pb-[150px]">
            <div className="flex w-full max-w-[600px] flex-col rounded-xl border border-secondary-90 bg-white shadow-blue-s">
                <div className="p-xl text-center">
                    <h1 className="text-2xl font-bold">
                        <FormattedMessage
                            id="_.credit_card.preferences.consent_review.connect_account"
                            defaultMessage="Connect your iwoca Credit Card account"
                        />
                    </h1>
                </div>

                <div className="max-h-[70vh] overflow-auto p-xl">
                    <div className="mb-2xl flex items-center justify-center gap-s">
                        <div className="flex h-3xl w-3xl items-center justify-center rounded-s bg-structure-80 text-s font-bold text-structure-20">
                            <FormattedMessage
                                id="_.credit_card.preferences.consent_review.generic_app_text"
                                defaultMessage="APP"
                            />
                        </div>
                        <div className="flex items-center gap-s">
                            <span className="h-xs w-xs rounded-full bg-structure-80" />
                            <span className="h-xs w-xs rounded-full bg-structure-80" />
                            <span className="h-xs w-xs rounded-full bg-structure-80" />
                        </div>
                        <IwocaLogo iconOnly className="h-3xl w-3xl" />
                    </div>
                    <p className="mb-m font-bold">
                        <FormattedMessage
                            id="_.credit_card.preferences.consent_review.share_details_header"
                            defaultMessage="What details am I sharing?"
                        />
                    </p>
                    <p className="font-reg">
                        <FormattedMessage
                            id="_.credit_card.preferences.consent_review.share_details"
                            defaultMessage={`The following information will be shared with {connectorName}:`}
                            values={{
                                connectorName: connectorName
                            }}
                        />
                    </p>
                    <ul className="font-normal mb-xs list-inside list-disc">
                        {permissions.map((permission) => (
                            <li
                                className="first-letter:uppercase"
                                key={permission}
                            >
                                {permission}
                            </li>
                        ))}
                    </ul>

                    <div
                        className={`my-xl rounded-s bg-structure-95 p-l pt-0 text-left`}
                    >
                        <p className="inline-flex items-center gap-s text-s font-med">
                            <IconShieldCheck />
                            <FormattedMessage
                                id="_.credit_card.preferences.consent_review.bank_grade_encryption"
                                defaultMessage="Bank-grade encryption"
                            />
                        </p>
                        <div className="mt-xs text-s font-reg">
                            <FormattedMessage
                                id="_.credit_card.preferences.consent_review.partner_description"
                                defaultMessage="We use a partner called tell.money to connect your iwoca credit card account. tell.money needs your permission to access your information. tell.money is FCA regulated, and won't share or use your personal data for anything else."
                            />
                        </div>
                    </div>

                    <div className="flex flex-col gap-s">
                        <Button
                            onClick={handleApprove}
                            variant="primary"
                            className="w-full"
                            disabled={isLoading}
                        >
                            <FormattedMessage
                                id="_.credit_card.preferences.consent_review.approve"
                                defaultMessage="Approve"
                            />
                        </Button>
                        <Button
                            onClick={handleDeny}
                            variant="tertiary"
                            className="w-full"
                            disabled={isLoading}
                        >
                            <FormattedMessage
                                id="_.credit_card.preferences.consent_review.deny"
                                defaultMessage="Deny"
                            />
                        </Button>
                    </div>

                    <p className="mt-l text-center text-s font-reg">
                        <FormattedMessage
                            id="_.credit_card.preferences.consent_review.footer"
                            defaultMessage="By continuing you agree to tell.money's {termsOfService} and {privacyPolicy}."
                            values={{
                                termsOfService: (
                                    <a
                                        target="_blank"
                                        className="text-s text-primary-40 underline"
                                        href="https://www.tell.money/website-terms/"
                                        rel="noreferrer"
                                    >
                                        <FormattedMessage
                                            id="_.credit_card.preferences.consent_review.terms_of_service"
                                            defaultMessage="Terms of Service"
                                        />
                                    </a>
                                ),
                                privacyPolicy: (
                                    <a
                                        target="_blank"
                                        className="text-s text-primary-40 underline"
                                        href="https://www.tell.money/privacy-policy/"
                                        rel="noreferrer"
                                    >
                                        <FormattedMessage
                                            id="_.credit_card.preferences.consent_review.privacy_policy"
                                            defaultMessage="Privacy Policy"
                                        />
                                    </a>
                                )
                            }}
                        />
                    </p>
                </div>
            </div>
        </div>
    );
};
